import { useContext } from 'react'
import { ApiServiceContext, CommonServices, CommonServicesContext } from '../services'
import { ApiService } from '../services/api'
import { BrowserService } from '../services/browser'
import { ExperimentService } from '../services/experiments'
import { UserService } from '../services/user'
import { UserJourneyService } from '../services/user-journey'
import { NinetailedPersonalizationService } from '../services/ninetailed-personalization'
import { extractLang, Language, Locale } from '@lib/utils/locale'
import { DEFAULT_LANG, DEFAULT_LOCALE } from '@lib/constants'
import { Localization } from '@lib/services/facade'

export const useCommonServices = (): CommonServices => {
  return useContext(CommonServicesContext)
}

export const useBrowserService = (): BrowserService => {
  return useCommonServices().browserService
}

export const useUserService = (): UserService => {
  return useCommonServices().userService
}

export const useExperimentService = (): ExperimentService => {
  return useCommonServices().experimentService
}

export const useUserJourneyService = (): UserJourneyService => {
  return useCommonServices().userJourneyService
}

export const useLanguage = (): Language => {
  const { localization } = useCommonServices().facadeService.get()

  const lang = extractLang(localization?.locale)
  const defaultLang = extractLang(localization?.defaultLocale)
  const supportedLangs = (
    localization ? [...new Set(localization.supportedLocales.map((item) => extractLang(item)))] : [DEFAULT_LANG]
  ) as Language['supportedLangs']

  return {
    lang,
    defaultLang,
    supportedLangs,
    isDefaultLang: lang === defaultLang,
  }
}

export const useLocalization = (): Localization => {
  const { localization } = useCommonServices().facadeService.get()

  const locale = (localization?.locale || DEFAULT_LOCALE) as Locale
  const defaultLocale = (localization?.defaultLocale || DEFAULT_LOCALE) as Locale
  const supportedLocales = (localization?.supportedLocales || [DEFAULT_LOCALE]) as Locale[]

  return {
    locale,
    defaultLocale,
    supportedLocales,
    isDefaultLocale: locale === defaultLocale,
  }
}

export const useApiService = (): ApiService => {
  return useContext(ApiServiceContext)
}

export const useNinetailedPersonalizationService = (): NinetailedPersonalizationService => {
  return useCommonServices().ninetailedPersonalizationService
}
